import { Box, Grid, Stack, Alert, Switch } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllRulesets, updateRulesetStatus } from 'wisestep-api';
import {
  WsPageHeader,
  WsTypography,
  WsTypoVariants,
  WsCard,
  CardVariant,
  Item,
  EmptyTeamsAndUsersIcon,
  WsDataGridPagination,
} from 'wisestep-base';

import { StyledPaginationBox } from '../../../../features/clientsSettings/components/OrganizationalUnitLayoutNew';
import { StyledDataGrid } from '../../../../features/teams/components/TeamListNew';
import EditIcon from '../../../../images/EditIcon';
import { JobAutomation, RulesetResponse, transformRulesetToJobAutomation } from '../../utils';

interface JobAutomationListingProps {
  title?: string;
}

export const JobAutomationListing = ({ title = 'Job Automations' }: JobAutomationListingProps) => {
  const navigate = useNavigate();

  const [rulesets, setRulesets] = useState<RulesetResponse[]>([]);
  const [filteredData, setFilteredData] = useState<JobAutomation[]>([]);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const transformedData = useMemo(() => {
    return rulesets.map(transformRulesetToJobAutomation);
  }, [rulesets]);

  useEffect(() => {
    setFilteredData(transformedData);
  }, [transformedData]);

  const handleEditAutomation = (rulesetId: string) => {
    navigate(`/automation/edit/${rulesetId}`);
  };

  useEffect(() => {
    const fetchRulesets = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAllRulesets();
        let rulesetsData: RulesetResponse[] = [];
        if (Array.isArray(res)) {
          rulesetsData = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          rulesetsData = res.data;
        }

        setRulesets(rulesetsData);
      } catch (err: any) {
        console.error('Error fetching IFTTT rulesets:', err);
        setError(err?.message || 'Failed to fetch job automations');
      } finally {
        setLoading(false);
      }
    };

    fetchRulesets();
  }, []);

  const getAllRuleset = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllRulesets();
      let rulesetsData: RulesetResponse[] = [];
      if (Array.isArray(res)) {
        rulesetsData = res;
      } else if (res && res.data && Array.isArray(res.data)) {
        rulesetsData = res.data;
      }

      setRulesets(rulesetsData);
    } catch (err: any) {
      console.error('Error fetching IFTTT rulesets:', err);
      setError(err?.message || 'Failed to fetch job automations');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (rulesetId: string, currentStatus: boolean) => {
    try {
      setRulesets((prev) =>
        prev.map((ruleset) =>
          ruleset.id === rulesetId ? { ...ruleset, active: !currentStatus } : ruleset
        )
      );

      await updateRulesetStatus(rulesetId, !currentStatus);
    } catch (err) {
      console.error('Error updating status:', err);
      setRulesets((prev) =>
        prev.map((ruleset) =>
          ruleset.id === rulesetId ? { ...ruleset, active: currentStatus } : ruleset
        )
      );
      setError('Failed to update status');
    }
  };

  const handleRefresh = () => {
    getAllRuleset();
  };

  const columns = useMemo(
    () => [
      {
        field: 'automationName',
        headerName: 'Automation Name',
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => (
          <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <WsTypography variant={WsTypoVariants.BODY1} bold>
              {params.value}
            </WsTypography>
          </Box>
        ),
      },
      {
        field: 'trigger',
        headerName: 'Trigger (Event)',
        flex: 1,
        minWidth: 180,
        renderCell: (params: any) => (
          <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <WsTypography variant={WsTypoVariants.BODY1}>{params.value}</WsTypography>
          </Box>
        ),
      },
      {
        field: 'conditions',
        headerName: 'Conditions (Rules)',
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => (
          <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <WsTypography variant={WsTypoVariants.BODY1}>{params.value}</WsTypography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        minWidth: 180,
        renderCell: (params: any) => (
          <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <WsTypography variant={WsTypoVariants.BODY1}>{params.value}</WsTypography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params: any) => (
          <Box display="flex" alignItems="center">
            <Switch
              checked={params.value === 'Active'}
              onChange={() => handleStatusToggle(params.row.id, params.value === 'Active')}
              color="primary"
              size="small"
            />
          </Box>
        ),
      },
      {
        field: 'actionsMenu',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ cursor: 'pointer' }} onClick={() => handleEditAutomation(params.row.id)}>
            <EditIcon />
          </Box>
        ),
      },
    ],
    []
  );

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {filteredData?.length ?? 0}
        </WsTypography>
      </Box>
      {filteredData && filteredData.length > pageSize && (
        <WsDataGridPagination
          count={Math.ceil(filteredData.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
        />
      )}
    </StyledPaginationBox>
  );

  const paginatedRows = useMemo(() => {
    const start = pageNo * pageSize;
    return filteredData.slice(start, start + pageSize).map((row, idx) => ({
      ...row,
      id: row?.id ?? idx,
    }));
  }, [filteredData, pageNo, pageSize]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item xs={12} m={-1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <WsPageHeader title={title} tooltip="" transparent buttonTitle="" />
        </Box>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert
            severity="error"
            action={
              <Box>
                <button onClick={handleRefresh} style={{ marginLeft: '10px' }}>
                  Retry
                </button>
              </Box>
            }
          >
            {error}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <WsTypography variant={WsTypoVariants.BODY1}>Loading job automations...</WsTypography>
          </Box>
        ) : !isEmpty(filteredData) ? (
          <>
            {paginationComponent}
            <StyledDataGrid
              autoHeight
              hideFooter
              rows={paginatedRows}
              columns={columns}
              page={pageNo}
              pageSize={pageSize}
              loading={loading}
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal !important',
                  wordWrap: 'break-word',
                  lineHeight: '1.4',
                  padding: '8px',
                },
                '& .MuiDataGrid-row': {
                  maxHeight: 'none !important',
                },
              }}
            />
            {filteredData.length > pageSize && paginationComponent}
          </>
        ) : (
          <WsCard
            title=""
            sx={{ mt: 4, width: '100%', boxShadow: 'none' }}
            isButton={false}
            cardType={CardVariant.WITH_LIGHT_BG}
            middleComponent={
              <Item>
                <Stack alignItems="center" spacing={3}>
                  <EmptyTeamsAndUsersIcon />
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    {transformedData.length === 0 ? 'No Job Automations Found' : 'No Results Found'}
                  </WsTypography>
                  {transformedData.length === 0 && !loading && (
                    <button onClick={handleRefresh}>Refresh</button>
                  )}
                </Stack>
              </Item>
            }
          />
        )}
      </Grid>
    </Grid>
  );
};

export default JobAutomationListing;
